import React, { useState, useEffect, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogFooter
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { 
  Play, 
  Pause, 
  Image as ImageIcon, 
  ChevronLeft, 
  ChevronRight, 
  Save, 
  Film,
  ZoomIn,
  ZoomOut,
  Grid3x3,
  AlertTriangle,
  Download
} from "lucide-react";
import { ScriptLine } from "../../../shared/schema";

interface Props {
  renderJobId: string;
  parentBlocks: ScriptLine[];
  existingImages: Set<string>;
  renderJobPages: number;
  currentVideoUrl?: string;
  onRegenerateVideo: () => void;
  onGenerateImage: (blockId: string, userPrompt: string | null) => Promise<string | null>;
}


const VideoEditor: React.FC<Props> = ({ renderJobId, parentBlocks, existingImages, renderJobPages, currentVideoUrl, onGenerateImage, onRegenerateVideo }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [blocks, setBlocks] = useState<ScriptLine[]>(parentBlocks);
  const [currentImageIndex, setCurrentImageIndex] = useState<number[]>(blocks.map(b => (b.imageUrl?.length || 1) - 1));
  const [zoomLevel, setZoomLevel] = useState(0.02); // px per ms
  const [playheadPosition, setPlayheadPosition] = useState(0);
  const [showImagePicker, setShowImagePicker] = useState(false);
  //const [customPrompt, setCustomPrompt] = useState(null); // we can allow the user to enter a custom prompt
  const [imagePage, setImagePage] = useState(0);
  const imagesPerPage = 8; // You can adjust this number
  const [selectedImageUrl, setSelectedImageUrl] = useState<string | null>(null);
  const [showReRenderConfirm, setShowReRenderConfirm] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const playStartTimestamp = useRef<number>(0);
  const playElapsed = useRef<number>(0); // time elapsed since current block started
  const [isGenerating, setIsGenerating] = useState(false);

  const timelineRef = useRef<HTMLDivElement>(null);
  

  useEffect(() => {
    setBlocks(parentBlocks);
  }, [parentBlocks]);


  const currentBlock = blocks[currentIndex];

  useEffect(() => {
    if (isPlaying && currentBlock) {
      const audio = new Audio(currentBlock.audioUrl);
      audioRef.current = audio;

      playStartTimestamp.current = performance.now();
      playElapsed.current = 0;

      audio.onended = () => {
        if (currentIndex + 1 < blocks.length) {
          setCurrentIndex(prev => prev + 1);
        } else {
          setIsPlaying(false);
        }
      };

      audio.play();
      animationFrameRef.current = requestAnimationFrame(updatePlayhead);
    }

    return () => {
      audioRef.current?.pause();
      audioRef.current = null;
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
    };
  }, [currentIndex, isPlaying]);

  const updatePlayhead = (timestamp: number) => {
    const elapsed = timestamp - playStartTimestamp.current;
    setPlayheadPosition(prev => prev + elapsed * zoomLevel);
    playStartTimestamp.current = timestamp;
    animationFrameRef.current = requestAnimationFrame(updatePlayhead);
  };

  const handlePlay = () => {
    if (!isPlaying) {
      setIsPlaying(true);
      const cumulative = blocks.slice(0, currentIndex).reduce((sum, b) => sum + (b.duration || 0), 0);
      setPlayheadPosition(cumulative * zoomLevel);
    }
  };

  const handlePause = () => {
    setIsPlaying(false);
    audioRef.current?.pause();
    if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
  };

  const handleGenerateImage = async () => {
  const currentBlock = blocks[currentIndex];
  const length = currentBlock?.imageUrl?.length || 1;
  setIsGenerating(true);
  try{
    const newImageUrl = await onGenerateImage(currentBlock.id, null);
    if (!newImageUrl) return;

    setBlocks(prev => {
        const updated = [...prev];
        const block = { ...updated[currentIndex] };
        block.imageUrl = [...(block.imageUrl || []), newImageUrl];
        updated[currentIndex] = block;
        return updated;
    });

    setCurrentImageIndex(prev => {
        const updated = [...prev];
        updated[currentIndex] = length; // now it's the newly added one
        return updated;
    });
  }
  finally{
    setIsGenerating(false);
  }
  
};


  const handleSaveImageSelection = async () => {
    const selectedIndex = currentImageIndex[currentIndex];
    const list = blocks[currentIndex].imageUrl || [];
    // const reordered = [...list];
    // const selected = reordered.splice(selectedIndex, 1)[0];
    // reordered.push(selected);
    await fetch(`/api/renders/save-image-order`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ imageUrl: list, blockId: blocks[currentIndex].id, renderJobId })
    });
    blocks[currentIndex].imageUrl = list;
    setCurrentImageIndex(prev => {
      const updated = [...prev];
      updated[currentIndex] = list.length - 1;
      return updated;
    });
  };

  // Calculate re-render cost
  const calculateReRenderCost = () => {
    return renderJobPages * 40;
  };

  // Handle re-render confirmation
  const handleReRenderConfirm = () => {
    setShowReRenderConfirm(false);
    onRegenerateVideo();
  };

  const totalDuration = blocks.reduce((sum, b) => sum + (b.duration || 0), 0);
  const timelineWidth = totalDuration * zoomLevel;

  let cumulativeDuration = 0;
  const pagedImages = Array.from(existingImages).slice(
    imagePage * imagesPerPage,
    (imagePage + 1) * imagesPerPage
    );

  return (
    <div className="space-y-6">
      {/* Main Preview Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Film className="h-5 w-5" />
              Video Editor
            </CardTitle>
            <Badge variant="outline">
              Block {currentIndex + 1} of {blocks.length}
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Image Preview */}
            <div className="relative bg-gray-100 rounded-lg overflow-hidden">
              <img
                src={currentBlock.imageUrl?.[currentImageIndex[currentIndex]] ?? ""}
                alt={`Block ${currentIndex + 1}`}
                className="w-full max-h-96 object-contain"
              />
            </div>

            {/* Image Navigation Controls */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Button
                variant={isPlaying ? "secondary" : "default"}
                onClick={() => {
                isPlaying ? handlePause() : handlePlay();
                }}
            >
                {isPlaying ? (
                <>
                    <Pause className="h-4 w-4 mr-2" />
                    Pause
                </>
                ) : (
                <>
                    <Play className="h-4 w-4 mr-2" />
                    Play
                </>
                )}
            </Button>

            
              </div>
              
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowImagePicker(true)}
                >
                  <Grid3x3 className="h-4 w-4 mr-2" />
                  Swap Current Image
                </Button>

                {/* Generate Image Button */}
                {isGenerating ? (
                <Button variant="outline" size="sm" disabled>
                    <svg className="animate-spin h-4 w-4 mr-2 text-gray-500" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a10 10 0 00-10 10h4z" />
                    </svg>
                    Generating...
                </Button>
                ) : (
                <Button variant="outline" size="sm" onClick={handleGenerateImage}>
                    <ImageIcon className="h-4 w-4 mr-2" />
                    Generate New
                </Button>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button
                variant="destructive"
                onClick={() => setShowReRenderConfirm(true)}
                >
                <Film className="h-4 w-4 mr-2" />
                Regenerate Video
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Timeline Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Timeline</CardTitle>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setZoomLevel(z => Math.max(z / 1.5, 0.001))}
              >
                <ZoomOut className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setZoomLevel(z => Math.min(z * 1.5, 1))}
              >
                <ZoomIn className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div
            ref={timelineRef}
            className="relative overflow-x-auto border border-gray-200 rounded-lg p-2 bg-gray-50"
            style={{ height: 80 }}
          >
            {blocks.map((block, i) => {
              let duration = block.duration;
              if(!duration || duration === 0){
                duration = 1000;
              }
              const width = Math.max(30, duration * zoomLevel);
              const left = cumulativeDuration * zoomLevel;
              cumulativeDuration += duration;
              const isActive = i === currentIndex;

              return (
                <div
                  key={block.id}
                  onClick={() => {
                    setCurrentIndex(i);
                    setIsPlaying(false);
                    setPlayheadPosition(left);
                  }}
                  className={`absolute cursor-pointer text-xs transition-colors duration-200 rounded p-1 ${
                    isActive 
                      ? 'bg-blue-600 text-white shadow-md' 
                      : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-100'
                  }`}
                  style={{
                    left,
                    width,
                    height: '60px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  <div className="font-medium truncate">
                    {block.text.slice(0, 20)}
                  </div>
                  <div className="text-xs opacity-75">
                    {((block.duration || 0) / 1000).toFixed(1)}s
                  </div>
                </div>
              );
            })}

            {/* Playhead */}
            <div
              className="absolute top-0 w-0.5 h-full bg-red-500 z-10"
              style={{ left: playheadPosition }}
            />
          </div>
        </CardContent>
      </Card>

      {/* Image Picker Modal */}
      <Dialog open={showImagePicker} onOpenChange={setShowImagePicker}>
        <DialogContent className="max-w-4xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>Select an Image</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 overflow-y-auto max-h-96">
            {pagedImages.map((url, idx) => (
            <div
                key={idx}
                className={`relative aspect-video cursor-pointer rounded-lg overflow-hidden border-2 transition-colors 
                    ${ selectedImageUrl === url ? 'border-blue-500' : 'border-transparent hover:border-blue-500'}`}
                onClick={() => {
                setSelectedImageUrl(url);
                }}
            >
                <img
                src={url}
                alt={`existing-${idx}`}
                className="w-full h-full object-cover"
                />
            </div>
            ))}
            
          </div>

          <div className="flex justify-center items-center gap-4 mt-4">
            <Button
                variant="outline"
                onClick={() => setImagePage(p => Math.max(p - 1, 0))}
                disabled={imagePage === 0}
            >
                Previous
            </Button>
            <span>Page {imagePage + 1}</span>
            <Button
                variant="outline"
                onClick={() =>
                setImagePage(p =>
                    (p + 1) * imagesPerPage < existingImages.size ? p + 1 : p
                )
                }
                disabled={(imagePage + 1) * imagesPerPage >= existingImages.size}
            >
                Next
            </Button>
            </div>

          <DialogFooter className="flex justify-between items-center w-full">
            <Button variant="outline" onClick={() => {
                setSelectedImageUrl(null);
                setShowImagePicker(false);
            }}>
                Cancel
            </Button>
            <Button
                variant="default"
                disabled={!selectedImageUrl}
                onClick={async () => {
                if (!selectedImageUrl) return;

                if (!blocks[currentIndex].imageUrl) {
                    blocks[currentIndex].imageUrl = [];
                }

                blocks[currentIndex].imageUrl.push(selectedImageUrl);

                // update displayed image index
                setCurrentImageIndex(prev => {
                    const updated = [...prev];
                    updated[currentIndex] = blocks[currentIndex].imageUrl!.length - 1;
                    return updated;
                });

                // call save method
                await handleSaveImageSelection();

                // cleanup
                setSelectedImageUrl(null);
                setShowImagePicker(false);
                setImagePage(0);
                }}
            >
                Replace Current Image
            </Button>
            </DialogFooter>

        </DialogContent>
      </Dialog>

      {/* Re-render Confirmation Dialog */}
      <Dialog open={showReRenderConfirm} onOpenChange={setShowReRenderConfirm}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-orange-500" />
              Confirm Video Re-render
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Re-rendering the video will overwrite the current video available for download. 
              Please download the current video here if you want to keep it now.
            </p>
            
            {currentVideoUrl && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <Button 
                  variant="outline" 
                  size="sm"
                  asChild
                  className="w-full"
                >
                  <a href={currentVideoUrl} download>
                    <Download className="h-4 w-4 mr-2" />
                    Download Current Video
                  </a>
                </Button>
              </div>
            )}
            
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
              <p className="text-sm font-medium text-orange-800">
                This new re-render will cost {calculateReRenderCost().toLocaleString()} points.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowReRenderConfirm(false)}
            >
              Cancel
            </Button>
            <Button 
              variant="destructive"
              onClick={handleReRenderConfirm}
            >
              Proceed with Re-render
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VideoEditor;
